import { useMemo, useRef, useState } from 'react';
import type { MutationResult } from '@/api/hooks/useLineItemPatch';
import { useBatchUndo } from '@/api/hooks/useBatchUndo';
import { useTransactionAudit, type TransactionAuditEntry } from '@/api/hooks/useTransactionAudit';

type HistoryRow = {
  entry: TransactionAuditEntry;
  affectedCount: number;
};

type UndoFeedback =
  | { batchId: string; kind: 'replay'; text: string }
  | { batchId: string; kind: 'success'; text: string }
  | { batchId: string; kind: 'unknown'; text: string }
  | { batchId: string; kind: 'changed_since'; text: string }
  | { batchId: string; kind: 'rejected'; text: string };

function groupAuditEntries(entries: readonly TransactionAuditEntry[]): HistoryRow[] {
  const grouped = new Map<string, HistoryRow>();
  for (const entry of entries) {
    const existing = grouped.get(entry.batch_id);
    if (existing) {
      existing.affectedCount += entry.affected_count;
      continue;
    }
    grouped.set(entry.batch_id, { entry, affectedCount: entry.affected_count });
  }
  return [...grouped.values()];
}

function relativeTime(value: string, now = new Date()): string {
  const elapsedSeconds = Math.round((now.getTime() - new Date(value).getTime()) / 1000);
  if (elapsedSeconds < 0) {
    const minutes = Math.max(1, Math.round(Math.abs(elapsedSeconds) / 60));
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (elapsedSeconds < 60) return 'Just now';
  const minutes = Math.round(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function absoluteTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('month')} ${get('day')}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} UTC`;
}

function outcomeLabel(outcome: TransactionAuditEntry['outcome']): string {
  if (outcome === 'applied') return 'Saved';
  if (outcome === 'undone') return 'Restored';
  return 'No change';
}

function isChangedSinceResult(result: MutationResult<unknown>): boolean {
  return result.kind === 'conflict' && result.code === 'changed_since';
}

function isUnknownResult(result: MutationResult<unknown>): boolean {
  return result.kind === 'unavailable';
}

export function HistoryList({
  canonicalTransactionId,
  onReload,
  onUndoSuccess,
}: {
  canonicalTransactionId: string;
  onReload?: () => void;
  onUndoSuccess?: () => void;
}) {
  const auditQuery = useTransactionAudit(canonicalTransactionId);
  const { undo } = useBatchUndo();
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [feedback, setFeedback] = useState<UndoFeedback | null>(null);
  const rows = useMemo(
    () => groupAuditEntries(auditQuery.data?.data ?? []),
    [auditQuery.data],
  );

  if (auditQuery.isPending) {
    return <div data-testid="history-list" role="status" className="mt-3 text-xs">Loading history…</div>;
  }

  if (auditQuery.isError || !auditQuery.data) {
    return (
      <div data-testid="history-list" role="alert" className="mt-3 text-xs text-degraded-foreground">
        History couldn&apos;t be loaded. Try again.
      </div>
    );
  }

  function focusUndo(batchId: string) {
    buttonRefs.current[batchId]?.focus();
  }

  function handleUndo(row: HistoryRow) {
    setFeedback(null);
    undo.mutate(
      { batchId: row.entry.batch_id },
      {
        onSuccess: (result) => {
          if (isChangedSinceResult(result)) {
            setFeedback({
              batchId: row.entry.batch_id,
              kind: 'changed_since',
              text: 'This changed since you last loaded it. Reload to see the latest.',
            });
          } else if (isUnknownResult(result)) {
            setFeedback({
              batchId: row.entry.batch_id,
              kind: 'unknown',
              text: "We couldn't confirm the undo. Reload to check the latest history.",
            });
          } else if (result.kind === 'replay') {
            setFeedback({
              batchId: row.entry.batch_id,
              kind: 'replay',
              text: 'Replay confirmed · this change was restored.',
            });
            onUndoSuccess?.();
          } else if (result.kind === 'success') {
            setFeedback({ batchId: row.entry.batch_id, kind: 'success', text: 'Restored.' });
            onUndoSuccess?.();
          } else {
            setFeedback({ batchId: row.entry.batch_id, kind: 'rejected', text: "Couldn't undo this change. Try again." });
          }
          focusUndo(row.entry.batch_id);
        },
        onError: (error) => {
          const code = typeof error === 'object' && error !== null && 'code' in error
            ? (error as { code?: unknown }).code
            : undefined;
          setFeedback({
            batchId: row.entry.batch_id,
            kind: code === 'changed_since' ? 'changed_since' : 'rejected',
            text: code === 'changed_since'
              ? 'This changed since you last loaded it. Reload to see the latest.'
              : "Couldn't undo this change. Try again.",
          });
          focusUndo(row.entry.batch_id);
        },
      },
    );
  }

  return (
    <div data-testid="history-list" className="mt-3 text-xs">
      <div className="font-semibold text-foreground">History</div>
      {rows.length === 0 ? (
        <p className="mt-1 text-muted-foreground">No history yet.</p>
      ) : (
        <ul className="mt-1 space-y-1.5 text-muted-foreground">
          {rows.map((row) => {
            const { entry } = row;
            const rowFeedback = feedback?.batchId === entry.batch_id ? feedback : null;
            const undoLabel = entry.undo.valid ? `Undo: ${entry.summary}` : 'Undo unavailable';
            return (
              <li key={entry.batch_id} data-testid="history-row" className="flex items-start justify-between gap-2 border-b border-border/60 py-2 last:border-b-0">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{entry.summary}</p>
                  <p>
                    {entry.actor_label} ·{' '}
                    <time dateTime={entry.happened_at}>
                      {relativeTime(entry.happened_at)} · {absoluteTime(entry.happened_at)}
                    </time>{' '}
                    · {outcomeLabel(entry.outcome)} · {row.affectedCount} {row.affectedCount === 1 ? 'item' : 'items'}
                  </p>
                  {rowFeedback && (
                    <p role={rowFeedback.kind === 'rejected' || rowFeedback.kind === 'changed_since' || rowFeedback.kind === 'unknown' ? 'alert' : 'status'} className="mt-1 text-degraded-foreground">
                      {rowFeedback.text}
                      {rowFeedback.kind === 'changed_since' && onReload && (
                        <button type="button" className="ml-2 min-h-11 rounded-md border border-input px-3 underline" onClick={onReload}>Reload</button>
                      )}
                    </p>
                  )}
                  {!entry.undo.valid && entry.undo.reason && <p className="mt-1">{entry.undo.reason}</p>}
                </div>
                <button
                  ref={(element) => { buttonRefs.current[entry.batch_id] = element; }}
                  type="button"
                  className="min-h-11 min-w-11 shrink-0 rounded-md border border-input px-3 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={undoLabel}
                  disabled={!entry.undo.valid || undo.isPending}
                  onClick={() => handleUndo(row)}
                >
                  {undo.isPending && entry.undo.valid ? 'Undoing…' : entry.undo.valid ? 'Undo' : 'Undo unavailable'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default HistoryList;
